import React from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { SunIcon, MoonIcon } from '../icons';

/**
 * Pill-shaped day/night toggle switch.
 *
 * Light mode:  [ DAYMODE  ☀ ]   — light track, knob slides right
 * Dark mode:   [ 🌙  NIGHTMODE ] — dark track, knob slides left
 */
const ThemeToggleButton = () => {
  const { isDark, toggleTheme } = useTheme();

  /* ── Dimensions ───────────────────────────────────── */
  const TRACK_W = 140;
  const TRACK_H = 36;
  const KNOB_SIZE = 30;
  const KNOB_OFFSET = 3; // gap between knob edge and track edge
  const ICON_SIZE = 16;

  /* ── Colours ──────────────────────────────────────── */
  const trackBg = isDark ? '#1e1e1e' : '#e0e0e0';
  const trackBorder = isDark ? '#333' : '#ccc';
  const knobBg = '#ffffff';
  const labelColor = isDark ? '#f5f5f5' : '#444';

  /* ── Knob position ────────────────────────────────── */
  const knobLeft = isDark
    ? KNOB_OFFSET
    : TRACK_W - KNOB_SIZE - KNOB_OFFSET;

  return (
    <button
      onClick={toggleTheme}
      aria-label="Toggle theme"
      title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
      style={{
        /* Reset */
        all: 'unset',
        cursor: 'pointer',
        /* Track */
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: TRACK_W,
        height: TRACK_H,
        borderRadius: TRACK_H / 2,
        background: trackBg,
        border: `1px solid ${trackBorder}`,
        padding: isDark
          ? `0 10px 0 ${KNOB_SIZE + KNOB_OFFSET + 6}px`   /* knob left → text right */
          : `0 ${KNOB_SIZE + KNOB_OFFSET + 6}px 0 10px`,  /* text left → knob right */
        boxSizing: 'border-box',
        transition: 'background 0.35s ease, border-color 0.35s ease',
        flexShrink: 0,
      }}
    >
      {/* ── Label text ─────────────────────────────── */}
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          color: labelColor,
          userSelect: 'none',
          whiteSpace: 'nowrap',
          lineHeight: 1,
          transition: 'color 0.3s ease',
        }}
      >
        {isDark ? 'NIGHTMODE' : 'DAYMODE'}
      </span>

      {/* ── Sliding knob with icon ─────────────────── */}
      <span
        style={{
          position: 'absolute',
          top: (TRACK_H - KNOB_SIZE) / 2,
          left: knobLeft,
          width: KNOB_SIZE,
          height: KNOB_SIZE,
          borderRadius: '50%',
          background: knobBg,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'left 0.35s cubic-bezier(.4,.2,.2,1)',
        }}
      >
        {isDark ? (
          <MoonIcon size={ICON_SIZE} style={{ color: '#333' }} />
        ) : (
          <SunIcon size={ICON_SIZE} style={{ color: '#f59e0b' }} />
        )}
      </span>
    </button>
  );
};

export default ThemeToggleButton;
